"use client";

import { motion, useReducedMotion } from "motion/react";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import type { CaseStudyKind, CaseStudySlug } from "@/config/case-studies";
import {
  CaseStudyKindIcon,
  IconSectionApproach,
  IconSectionContext,
  IconSectionOutcomes,
} from "./case-study-icons";

const labelClass =
  "text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] font-semibold text-[var(--primary)]";

const paragraphClass =
  "text-[var(--on-surface-variant)] leading-relaxed text-[1rem] sm:text-[1.0625rem]";

const sectionTitleClass =
  "font-display text-xl sm:text-2xl text-[var(--on-surface)] tracking-tight leading-snug";

const easeOutQuart = [0.25, 1, 0.5, 1] as const;
const easeOutQuint = [0.22, 1, 0.36, 1] as const;

export function CaseStudyDetailHeroClient({
  backLink,
  tagline,
  title,
  kind,
  productSiteUrl,
  visitProductWebsite,
}: {
  backLink: string;
  tagline: string;
  title: string;
  kind: CaseStudyKind;
  productSiteUrl?: string;
  visitProductWebsite?: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <>
      <Link
        href="/case-studies"
        className="text-sm text-[var(--on-surface-variant)] hover:text-[var(--primary)] mb-6 sm:mb-8 inline-block motion-safe:transition-colors motion-safe:duration-200"
        style={{ fontFamily: "var(--font-body)" }}
      >
        {backLink}
      </Link>
      <span
        className={`${labelClass} mb-6 sm:mb-8 block`}
        style={{ fontFamily: "var(--font-body)" }}
      >
        {tagline}
      </span>
      <motion.span
        className="mb-5 sm:mb-6 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[color-mix(in_srgb,var(--primary-container)_95%,transparent)] text-[var(--primary)] ring-1 ring-[color-mix(in_srgb,var(--outline-variant)_20%,transparent)]"
        aria-hidden
        initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: reduceMotion ? 0 : 0.5,
          ease: easeOutQuint,
        }}
      >
        <CaseStudyKindIcon kind={kind} className="h-[22px] w-[22px]" />
      </motion.span>
      <motion.h1
        className={cn(
          "font-display text-[clamp(2rem,5.5vw,3.25rem)] md:text-[clamp(2.5rem,4.5vw,3.75rem)] leading-[1.1] tracking-tight text-[var(--on-surface)]",
          productSiteUrl && visitProductWebsite ? "mb-3 sm:mb-4" : "mb-6 sm:mb-8",
        )}
        style={{ fontFamily: "var(--font-display)" }}
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: reduceMotion ? 0 : 0.5,
          delay: reduceMotion ? 0 : 0.06,
          ease: easeOutQuart,
        }}
      >
        {title}
      </motion.h1>
      {productSiteUrl && visitProductWebsite ? (
        <motion.p
          className="mb-6 sm:mb-8"
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: reduceMotion ? 0 : 0.45,
            delay: reduceMotion ? 0 : 0.1,
            ease: easeOutQuart,
          }}
        >
          <a
            href={productSiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--primary)] hover:underline underline-offset-[0.25rem] decoration-[color-mix(in_srgb,var(--primary)_55%,transparent)] motion-safe:transition-colors"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {visitProductWebsite}
            <span aria-hidden className="text-[0.85em] opacity-80">
              ↗
            </span>
          </a>
        </motion.p>
      ) : null}
    </>
  );
}

export function CaseStudyDetailNarrativeClient({
  slug,
  challengeHeading,
  approachHeading,
  outcomesHeading,
  challenge,
  approach,
  outcomes,
  metaLabel,
  stack,
}: {
  slug: CaseStudySlug;
  challengeHeading: string;
  approachHeading: string;
  outcomesHeading: string;
  challenge: string;
  approach: string;
  outcomes: string;
  metaLabel: string;
  stack: string[];
}) {
  const reduceMotion = useReducedMotion();

  const item = {
    hidden: reduceMotion
      ? { opacity: 1, y: 0 }
      : { opacity: 0, y: 16 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: reduceMotion ? 0 : 0.42,
        ease: easeOutQuart,
      },
    },
  };

  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.1,
        delayChildren: reduceMotion ? 0 : 0.04,
      },
    },
  };

  return (
    <>
      <motion.div
        className="lg:col-span-7 xl:col-span-6 max-w-[720px] lg:max-w-none space-y-12 sm:space-y-14 md:space-y-16"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-40px 0px" }}
        variants={container}
      >
        <motion.section variants={item} aria-labelledby={`${slug}-challenge`}>
          <div className="flex gap-3 sm:gap-4">
            <IconSectionContext className="h-5 w-5 sm:h-6 sm:w-6 shrink-0 mt-0.5" />
            <div className="min-w-0 space-y-4 sm:space-y-5">
              <h2
                id={`${slug}-challenge`}
                className={sectionTitleClass}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {challengeHeading}
              </h2>
              <p
                className={`${paragraphClass} max-w-[40rem]`}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {challenge}
              </p>
            </div>
          </div>
        </motion.section>

        <motion.section variants={item} aria-labelledby={`${slug}-approach`}>
          <div className="flex gap-3 sm:gap-4">
            <IconSectionApproach className="h-5 w-5 sm:h-6 sm:w-6 shrink-0 mt-0.5" />
            <div className="min-w-0 space-y-4 sm:space-y-5">
              <h2
                id={`${slug}-approach`}
                className={sectionTitleClass}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {approachHeading}
              </h2>
              <p
                className={`${paragraphClass} max-w-[40rem]`}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {approach}
              </p>
            </div>
          </div>
        </motion.section>

        <motion.section variants={item} aria-labelledby={`${slug}-outcomes`}>
          <div className="flex gap-3 sm:gap-4">
            <IconSectionOutcomes className="h-5 w-5 sm:h-6 sm:w-6 shrink-0 mt-0.5" />
            <div className="min-w-0 space-y-4 sm:space-y-5">
              <h2
                id={`${slug}-outcomes`}
                className={sectionTitleClass}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {outcomesHeading}
              </h2>
              <p
                className={`${paragraphClass} max-w-[40rem]`}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {outcomes}
              </p>
            </div>
          </div>
        </motion.section>
      </motion.div>

      <motion.aside
        className="mt-12 lg:mt-0 lg:col-span-4 xl:col-span-5 lg:col-start-9 xl:col-start-8"
        initial={reduceMotion ? false : { opacity: 0, x: 18 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-40px 0px" }}
        transition={{
          duration: reduceMotion ? 0 : 0.48,
          delay: reduceMotion ? 0 : 0.12,
          ease: easeOutQuart,
        }}
      >
        <div
          className="rounded-[var(--radius-sm)] bg-[var(--surface-container)] p-5 sm:p-6 motion-safe:transition-shadow motion-safe:duration-300 motion-safe:hover:shadow-[0_0_40px_rgba(225,225,247,0.09)]"
          style={{
            boxShadow: "0 0 40px rgba(225, 225, 247, 0.06)",
          }}
        >
          <div className="rounded-[var(--radius-sm)] bg-[var(--surface-container-lowest)] p-4 sm:p-5 ring-1 ring-[color-mix(in_srgb,var(--outline-variant)_15%,transparent)]">
            <h3
              className={`${labelClass} mb-4 text-[var(--on-surface)]`}
              style={{ fontFamily: "var(--font-body)" }}
            >
              {metaLabel}
            </h3>
            <ul className="space-y-3">
              {stack.map((line, i) => (
                <motion.li
                  key={line}
                  className="flex gap-2.5 text-[var(--on-surface-variant)] text-[0.8125rem] sm:text-sm leading-snug"
                  style={{ fontFamily: "var(--font-body)" }}
                  initial={reduceMotion ? false : { opacity: 0, x: 6 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: reduceMotion ? 0 : 0.35,
                    delay: reduceMotion ? 0 : 0.08 + i * 0.05,
                    ease: easeOutQuart,
                  }}
                >
                  <span
                    className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[var(--primary)]"
                    aria-hidden
                  />
                  {line}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </motion.aside>
    </>
  );
}
